/**
 * OperatingModeShell · Jul 2026
 * ================================================================
 * The single global mount-point for all Operating-mode-only UI:
 *   • OperatingCommandBar (docked strip — hosts the Log Revenue /
 *     Log Snapshot / Weekly Review / Cohort / Experiments / What's
 *     due actions AND the ⌘K palette trigger)
 *   • CommandPalette (⌘K)
 *   • LogThisMonthModal (opened by the bar's log actions)
 *
 * (Jul 2026: the standalone bottom-right LogThisMonthFAB was removed
 * because it duplicated the bar's Log revenue / Log snapshot buttons
 * AND overlapped the Founder Mentor launcher at `bottom-6 right-6`.)
 *
 * Wiring pattern mirrors <LegFixReturnBanner /> — mounted once in
 * <AppRouter />, self-discovers the current business by parsing the
 * URL (path segment or `?business=` query), fetches the doc, and
 * only renders the shell when `business.mode === "operating"`.
 *
 * Skips the auth / landing routes so we never call the API without
 * a session.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../../lib/apiClient";
import { isOperatingMode } from "../../lib/stage";
import { extractBusinessId } from "../LegFixReturnBanner";
import OperatingCommandBar from "./OperatingCommandBar";
import LaunchCommandBar from "./LaunchCommandBar";
import CommandPalette from "./CommandPalette";
import LogThisMonthModal from "./LogThisMonthModal";
import { LOG_ACTUALS_EVENT } from "../../lib/openLogActualsDialog";
import { fetchBusinessWithRetry } from "../../lib/fetchBusinessWithRetry";

// Routes where mounting the shell is inappropriate — auth, landing,
// admin surfaces, wizard (still in questionnaire), etc. Cheap
// prefix check.
const EXCLUDED_PREFIXES = [
  "/login",
  "/signup",
  "/register",
  "/forgot",
  "/reset",
  "/otp",
  "/admin",
  "/wizard",
  "/onboarding",
  "/pricing",
  "/kb",
  "/help",
  "/guest",
  "/share",
];

function isExcluded(pathname) {
  if (!pathname || pathname === "/") return true;
  return EXCLUDED_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`) || pathname.startsWith(`${p}?`),
  );
}

export default function OperatingModeShell() {
  const location = useLocation();
  const businessId = useMemo(
    () => extractBusinessId(location.pathname, location.search),
    [location.pathname, location.search],
  );

  const excluded = isExcluded(location.pathname);
  const [business, setBusiness] = useState(null);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [initialTab, setInitialTab] = useState("revenue");

  // Fetch the business whenever the id changes and we're on a valid
  // route. Guard against races by tracking the id we asked for.
  useEffect(() => {
    if (excluded || !businessId) {
      setBusiness(null);
      return () => {};
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await fetchBusinessWithRetry(api, businessId);
        if (!cancelled) setBusiness(data);
      } catch {
        if (!cancelled) setBusiness(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [businessId, excluded, location.pathname]);

  // ⌘K / Ctrl+K globally — but only when the shell is actively
  // rendered (Operating mode + a business id resolved).
  const shellActive = !excluded && business && isOperatingMode(business);
  useEffect(() => {
    if (!shellActive) return undefined;
    const onKey = (e) => {
      const isPalette =
        (e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K");
      if (isPalette) {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [shellActive]);

  // Jun 2026 · Launch-mode ⌘K — same quick-search shortcut for
  // launching businesses (separate effect so the operating-mode gate
  // above stays byte-identical for its regression tests).
  const launchActive = !excluded && !!business && !!businessId && !isOperatingMode(business);
  useEffect(() => {
    if (!launchActive) return undefined;
    const onKey = (e) => {
      const isPalette =
        (e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K");
      if (isPalette) {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [launchActive]);

  const openLog = useCallback((tab) => {
    setInitialTab(tab === "snapshot" ? "snapshot" : "revenue");
    setModalOpen(true);
  }, []);

  // Feb 2027 · Global "open Log Actuals dialog" bridge. Any surface
  // in the app can dispatch `tlb:open-log-actuals` (see
  // `lib/openLogActualsDialog.js`) and the shell will open the
  // dialog with the requested tab. `evt.preventDefault()` signals
  // "handled" so the caller can skip its fallback (a plain navigate
  // to /business/:id/log-actuals). Only wired while the shell is
  // active so a launching-mode business never accidentally hijacks
  // navigation to the standalone page.
  useEffect(() => {
    if (!shellActive) return undefined;
    const onOpenLog = (evt) => {
      const tab = evt?.detail?.tab === "snapshot" ? "snapshot" : "revenue";
      openLog(tab);
      if (typeof evt.preventDefault === "function") evt.preventDefault();
    };
    window.addEventListener(LOG_ACTUALS_EVENT, onOpenLog);
    return () => window.removeEventListener(LOG_ACTUALS_EVENT, onOpenLog);
  }, [shellActive, openLog]);

  const handleLogged = useCallback(async () => {
    if (!businessId) return;
    try {
      const { data } = await fetchBusinessWithRetry(api, businessId, { force: true });
      setBusiness(data);
    } catch {
      // no-op — the toast already told the founder it saved.
    }
  }, [businessId]);

  if (!shellActive) {
    // Jun 2026 · Launch-mode sibling — businesses still in LAUNCHING
    // mode get their own slim command bar (Business Plan / Roadmap /
    // Log actuals / Weekly Review / Compliance + tappable Launch
    // readiness score) AND the same ⌘K quick-search palette.
    if (launchActive) {
      return (
        <>
          <LaunchCommandBar
            businessId={businessId}
            business={business}
            onOpenPalette={() => setPaletteOpen(true)}
            onBusinessUpdate={handleLogged}
          />
          <CommandPalette
            open={paletteOpen}
            onClose={() => setPaletteOpen(false)}
            businessId={businessId}
            business={business}
            handlers={{ openLog }}
          />
          <LogThisMonthModal
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            business={business}
            onLogged={handleLogged}
            initialTab={initialTab}
          />
        </>
      );
    }
    return null;
  }

  return (
    <>
      <OperatingCommandBar
        businessId={businessId}
        business={business}
        onOpenPalette={() => setPaletteOpen(true)}
        onOpenLog={openLog}
        onBusinessUpdate={handleLogged}
      />
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        businessId={businessId}
        business={business}
        handlers={{ openLog }}
      />
      {/* Modal is mounted at the shell level so the command bar's
          Log revenue / Log snapshot actions can open it without
          owning their own state. The standalone FAB was removed
          Jul 2026 — it duplicated the two bar actions and
          overlapped the bottom-right Founder Mentor launcher. */}
      <LogThisMonthModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        business={business}
        onLogged={handleLogged}
        initialTab={initialTab}
      />
      {/* Aug 2026 · Legacy floating <ReturnToWeeklyReviewPill /> removed;
          the pill was rewritten as an inline button and now lives on
          the Hub header strip next to the Compact-hub toggle. */}
    </>
  );
}
